import React from 'react';
import { Card, Row, Col, Button, Tag, Spin, Tooltip, Empty, Alert, Collapse, Typography, Table } from 'antd';
import {
	ExclamationCircleOutlined, CheckCircleOutlined, RedoOutlined,
	SafetyCertificateOutlined, WarningOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { initHandler, ajaxViewHandler, setConfig } from '../../store/actions';
import { Helmet } from 'react-helmet';
import Config from '../../Config';

const { Text } = Typography;
const { Panel } = Collapse;

// Laporan permanen - lihat memori proyek
// project_gudang_notice_cegah_tumpang_tindih_produksi.md. Query LIVE (bukan
// snapshot audit manual) sama persis dengan audit yang pernah dijalankan
// sekali di awal utk menemukan masalah ini pertama kali.
const configClass = {
	title: 'Laporan Overlap Kode Notice',
	apiUrl: {
		laporan: 'gudangnotice/api/box/laporan-overlap-notice/',
	},
	breadCrumb: ['Laporan', 'Overlap Kode Notice'],
};

const config = new Config();

class LapOverlapNotice extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			isMobile: window.innerWidth <= 768,
			loading: false,
			ringkasan: null,
			kodeList: [],
		};
	}

	UNSAFE_componentWillMount() {
		this.props.initHandler();
	}

	componentDidMount() {
		this._handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });
		window.addEventListener('resize', this._handleResize);
		this.fetchLaporan();
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._handleResize);
	}

	fetchLaporan = () => {
		this.setState({ loading: true });
		this.props.ajaxViewHandler('get', configClass.apiUrl.laporan)
			.then(() => {
				if (this.props.isSuccess) {
					const data = this.props.responseMsg || {};
					this.setState({ ringkasan: data.ringkasan || null, kodeList: data.kode_list || [] });
				}
				this.setState({ loading: false });
			}).catch(() => this.setState({ loading: false }));
	};

	// Kartu ringkasan atas - 3 angka besar, bahasa non-teknis (bukan istilah
	// query/database) supaya langsung dipahami Kantor Induk tanpa penjelasan
	// tambahan.
	_renderRingkasanCards() {
		const { ringkasan } = this.state;
		if (!ringkasan) return null;
		const cards = [
			{ label: 'Total Kode Notice Pernah Dipakai', value: ringkasan.total_kode, color: '#0f2847', icon: <SafetyCertificateOutlined /> },
			{ label: 'Bermasalah - Jangan Dipakai Lagi', value: ringkasan.jumlah_overlap, color: '#cf1322', icon: <WarningOutlined /> },
			{ label: 'Aman Digunakan', value: ringkasan.jumlah_aman, color: '#389e0d', icon: <CheckCircleOutlined /> },
		];
		return (
			<Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
				{cards.map((c) => (
					<Col key={c.label} xs={24} sm={8}>
						<Card style={{ borderRadius: 14, border: 'none', boxShadow: '0 4px 20px rgba(15,40,71,0.08)' }} bodyStyle={{ padding: 20 }}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
								<div style={{
									width: 48, height: 48, borderRadius: 12, flexShrink: 0,
									background: `${c.color}14`, color: c.color,
									display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
								}}>
									{c.icon}
								</div>
								<div>
									<div style={{ fontSize: 26, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
									<div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{c.label}</div>
								</div>
							</div>
						</Card>
					</Col>
				))}
			</Row>
		);
	}

	// Header 1 baris per kode dalam Collapse - bahasa non-teknis, bukan
	// istilah "overlap pairs" dsb. Badge warna sesuai kondisi.
	_renderKodeHeader(k) {
		const bermasalah = k.kondisi === 'overlap';
		const label = bermasalah
			? `Bermasalah - dipakai tumpang tindih oleh ${k.upt_list.filter((u) => u.terlibat_overlap).length} UPT`
			: (k.jumlah_upt > 1 ? 'Aman - dipakai beberapa UPT tapi rentang nomor tidak beririsan' : 'Aman - hanya dipakai 1 UPT');
		return (
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, width: '100%' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					<span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 15, color: '#0f2847' }}>{k.kode_notice}</span>
					<Tag
						icon={bermasalah ? <WarningOutlined /> : <CheckCircleOutlined />}
						style={{
							borderRadius: 20, fontWeight: 600, margin: 0,
							background: bermasalah ? '#fff1f0' : '#f6ffed',
							borderColor: bermasalah ? '#ffa39e' : '#b7eb8f',
							color: bermasalah ? '#cf1322' : '#389e0d',
						}}
					>
						{label}
					</Tag>
				</div>
				<span style={{ fontSize: 12, color: '#999' }}>{k.jumlah_upt} UPT pernah pakai</span>
			</div>
		);
	}

	_renderKodeDetailTable(k) {
		const columns = [
			{ title: 'UPT', dataIndex: 'upt_nama', key: 'upt_nama',
				render: (v, row) => (
					<span style={{ fontWeight: row.terlibat_overlap ? 700 : 400, color: row.terlibat_overlap ? '#cf1322' : undefined }}>
						{v}{row.terlibat_overlap && <ExclamationCircleOutlined style={{ marginLeft: 6 }} />}
					</span>
				) },
			{ title: 'Rentang Nomor Notice Terpakai', key: 'rentang', width: 260,
				render: (_, row) => <Text style={{ fontFamily: 'monospace' }}>{String(row.nomor_awal).padStart(7, '0')} – {String(row.nomor_akhir).padStart(7, '0')}</Text> },
			{ title: 'Jumlah Transaksi', dataIndex: 'jumlah_transaksi', key: 'jumlah_transaksi', width: 140, align: 'center' },
		];
		return (
			<Table
				size="small"
				pagination={false}
				rowKey="upt_id"
				dataSource={k.upt_list}
				columns={columns}
				rowClassName={(row) => (row.terlibat_overlap ? 'overlap-row-bermasalah' : '')}
			/>
		);
	}

	render() {
		const { loading, ringkasan, kodeList, isMobile } = this.state;
		return (
			<div>
				<Helmet>
					<title>{configClass.title + ' | ' + config.get_site_info().name}</title>
					<style>{`
						.overlap-row-bermasalah > td { background: #fff7f6 !important; }
						.overlap-notice-collapse .ant-collapse-header { align-items: center !important; }
					`}</style>
				</Helmet>

				<Card style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', overflow: 'hidden', marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
					<div style={{
						padding: '16px 20px',
						background: 'linear-gradient(135deg, #0f2847, #1e4d8a)',
						display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
					}}>
						<div style={{ color: '#fff', fontWeight: 700, fontSize: 17 }}>
							<SafetyCertificateOutlined style={{ marginRight: 8 }} />
							{configClass.title}
						</div>
						<Tooltip title="Refresh data">
							<Button icon={<RedoOutlined />} style={{ borderRadius: 8, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}
								onClick={this.fetchLaporan} loading={loading}>
								{!isMobile && 'Refresh'}
							</Button>
						</Tooltip>
					</div>
					<div style={{ padding: 20 }}>
						<Alert
							type="info"
							showIcon
							style={{ marginBottom: 16 }}
							message="Kegunaan laporan ini"
							description="Menunjukkan kode notice mana yang PERNAH terpakai tumpang tindih (nomor notice yang sama, kode yang sama, dipakai lebih dari 1 UPT) di histori transaksi. Kode yang ditandai Bermasalah SEBAIKNYA TIDAK dipakai lagi untuk migrasi legacy / tambah stok box UPT baru - gunakan kode lain yang berstatus Aman."
						/>
						{loading && !ringkasan ? (
							<div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
						) : (
							<>
								{this._renderRingkasanCards()}
								{kodeList.length === 0 ? (
									<Empty description="Belum ada data transaksi bernomor notice" />
								) : (
									<Collapse className="overlap-notice-collapse" bordered={false}>
										{kodeList.map((k) => (
											<Panel
												key={k.kode_notice}
												style={{
													marginBottom: 8, borderRadius: 10, overflow: 'hidden',
													border: k.kondisi === 'overlap' ? '1px solid #ffccc7' : '1px solid #e2e8f0',
												}}
												header={this._renderKodeHeader(k)}
											>
												{this._renderKodeDetailTable(k)}
											</Panel>
										))}
									</Collapse>
								)}
							</>
						)}
					</div>
				</Card>
			</div>
		);
	}
}

const mapStateToProps = function (state) {
	return state.configReducers;
};

export default connect(
	mapStateToProps,
	{ initHandler, setConfig, ajaxViewHandler }
)(LapOverlapNotice);
